<template>
  <Header />
  <SideBar />
  <main id="main-content" class="ml-64 p-8">
    <!-- Page Header -->
    <div class="mb-8 flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-green-800">Services</h2>
        <p class="text-gray-600 text-sm mt-1">Manage available services and subscribe organizations</p>
      </div>
      <button @click="showSubscribeModal = true"
        class="bg-gradient-to-r from-green-700 to-green-800 hover:from-green-700 hover:to-green-700
        text-white px-5 py-2.5 rounded-lg flex items-center font-medium focus:outline-none
        focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200
        transform hover:scale-[1.02]">
        <i class="fa-solid fa-plus mr-2"></i>
        Subscribe Organization
      </button>
    </div>

    <!-- Services Table -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div v-if="servicesStore.loading" class="flex justify-center items-center h-48">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <div v-else-if="!servicesStore.services?.length" class="p-12 text-center text-gray-400">
        <i class="fa-solid fa-cubes text-4xl mb-3"></i>
        <p class="text-sm">No services found</p>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="service in servicesStore.services" :key="service.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center space-x-3">
                  <div class="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i class="fa-solid fa-cube text-green-700 text-xs"></i>
                  </div>
                  <span class="text-sm font-medium text-gray-900">{{ service.name }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="service.status === 'Active' || service.status === 'ACTIVE'
                  ? 'px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full'
                  : 'px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full'">
                  {{ service.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(service.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button @click="openSubscribeForService(service)" title="Subscribe organization"
                  class="text-green-700 hover:text-green-900">
                  <i class="fa-solid fa-link"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Subscribe Organization Modal -->
    <Modal :show="showSubscribeModal" title="Subscribe Organization to Service" size="max-w-lg" @close="closeSubscribeModal">
      <div v-if="submitLoading" class="flex justify-center items-center h-32">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <form v-else class="space-y-5" @submit.prevent="handleSubscribe">
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-600">Organization</label>
          <select v-model="subscribeForm.organizationId" required
            class="block w-full pl-4 pr-3 py-3 border border-gray-300 rounded-sm text-gray-600
            focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white">
            <option value="" disabled>Select an organization</option>
            <option v-for="org in organizationStore.organizationResults" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-600">Service</label>
          <select v-model="subscribeForm.serviceId" required
            class="block w-full pl-4 pr-3 py-3 border border-gray-300 rounded-sm text-gray-600
            focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white">
            <option value="" disabled>Select a service</option>
            <option v-for="svc in servicesStore.services" :key="svc.id" :value="svc.id">
              {{ svc.name }}
            </option>
          </select>
        </div>
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-600">Custom Rate <span class="text-gray-400 font-normal">(optional)</span></label>
          <input v-model="subscribeForm.rate" type="number" step="any" min="0"
            class="block w-full pl-4 pr-3 py-3 border border-gray-300 rounded-sm text-gray-600
            placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500
            focus:border-transparent" placeholder="Leave blank to use default rate" />
          <p class="text-gray-400 text-xs">If left blank, the service's default rate will apply</p>
        </div>
        <div class="flex justify-end space-x-2 pt-2">
          <button type="button" @click="closeSubscribeModal"
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300 text-sm">
            Cancel
          </button>
          <button type="submit"
            class="bg-gradient-to-r from-green-700 to-green-800 text-white px-6 py-2 rounded-md
            flex items-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2
            focus:ring-green-500 transition-all duration-200 disabled:opacity-50 text-sm">
            <i class="fa-solid fa-link mr-2"></i>
            Subscribe
          </button>
        </div>
      </form>
    </Modal>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useServicesStore } from '@/store/services';
import { useOrganizationStore } from '@/store/organizations';
import { useNotificationStore } from '@/store/notifications';
import type { Service } from '@/config/types/responses';
import Header from '@/components/header.vue';
import SideBar from '@/components/sidebar.vue';
import Modal from '@/components/modal.vue';

const servicesStore = useServicesStore();
const organizationStore = useOrganizationStore();
const notificationStore = useNotificationStore();

const showSubscribeModal = ref(false);
const submitLoading = ref(false);

const subscribeForm = ref({
  organizationId: '',
  serviceId: '',
  rate: '',
});

onMounted(async () => {
  await servicesStore.fetchServices();
  await organizationStore.fetchOrganizations(1);
});

function formatDate(input: string) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(d);
}

function openSubscribeForService(service: Service) {
  subscribeForm.value.serviceId = service.id;
  showSubscribeModal.value = true;
}

function closeSubscribeModal() {
  showSubscribeModal.value = false;
  subscribeForm.value = { organizationId: '', serviceId: '', rate: '' };
  servicesStore.error = null;
}

async function handleSubscribe() {
  if (!subscribeForm.value.organizationId || !subscribeForm.value.serviceId) {
    notificationStore.showError('Validation Error', 'Please select both an organization and a service.');
    return;
  }
  submitLoading.value = true;
  const ok = await servicesStore.createSubscription(
    subscribeForm.value.organizationId,
    subscribeForm.value.serviceId,
    subscribeForm.value.rate || undefined,
  );
  submitLoading.value = false;
  if (ok) {
    notificationStore.showSuccess('Subscribed', servicesStore.success ?? 'Subscription created.');
    closeSubscribeModal();
  } else {
    notificationStore.showError('Subscription Failed', servicesStore.error ?? 'An error occurred.');
  }
}
</script>
